import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// API Configuration
// Detect if we're on a custom domain and use it for API calls
const getApiBaseUrl = (): string => {
  // Check if we're on a custom domain (not feedbackgate.com)
  const hostname = window.location.hostname.toLowerCase();
  const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || '';
  
  // If on custom domain, use same domain for API (assuming API is on same domain or subdomain)
  if (hostname && 
      !hostname.includes('feedbackgate.com') && 
      !hostname.includes('localhost') && 
      !hostname.includes('127.0.0.1')) {
    // Use same protocol and domain, assume API is on /api or api subdomain
    const protocol = window.location.protocol;
    // Try api subdomain first, fallback to /api path
    if (hostname.startsWith('app.') || hostname.startsWith('www.')) {
      // Replace app/www with api
      const apiHost = hostname.replace(/^(app|www)\./, 'api.');
      return `${protocol}//${apiHost}`;
    } else {
      // Use same domain with /api path
      return `${protocol}//${hostname}/api`;
    }
  }
  
  return defaultApiUrl;
};

const API_BASE_URL = getApiBaseUrl();
const API_TIMEOUT = 60000; // 60 seconds - increased for development

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const tokenManager = {
  getAccessToken: (): string | null => {
    return localStorage.getItem('access_token');
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refresh_token');
  },
  
  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  },
  
  clearTokens: (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
  
  getTenantId: (): string | null => {
    return localStorage.getItem('tenant_id');
  },
  
  setTenantId: (tenantId: string): void => {
    localStorage.setItem('tenant_id', tenantId);
  },
  
  clearTenantId: (): void => {
    localStorage.removeItem('tenant_id');
  },
};

// Request interceptor to add auth headers
apiClient.interceptors.request.use(
  (config) => {
    // Check if this is a public endpoint (no auth required)
    // Check both the config flag and URL patterns
    const url = config.url || '';
    const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url;
    const isPublicEndpoint = (config as any).isPublic || 
                             url.includes('/rating-gate/settings/public') ||
                             url.includes('/rating-gate/submit') ||
                             fullUrl.includes('/rating-gate/settings/public') ||
                             fullUrl.includes('/rating-gate/submit');
    
    const accessToken = tokenManager.getAccessToken();
    const tenantId = tokenManager.getTenantId();
    
    // Only add auth headers if not a public endpoint
    if (!isPublicEndpoint) {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      
      if (tenantId) {
        config.headers['X-Tenant-Id'] = tenantId;
      }
    }
    
    // Mark public endpoints for response interceptor
    (config as any).isPublic = isPublicEndpoint;
    
    // Add request timestamp for debugging (using custom property)
    (config as any).metadata = { startTime: new Date().getTime() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response time for debugging
    const metadata = (response.config as any).metadata;
    if (metadata?.startTime) {
      const duration = new Date().getTime() - metadata.startTime;
      console.debug(`API Request to ${response.config.url} took ${duration}ms`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log error details for debugging (but not for expected 401 auth errors)
    if (originalRequest?.metadata?.startTime && error.response?.status !== 401) {
      const duration = new Date().getTime() - originalRequest.metadata.startTime;
      console.error(`API Request to ${originalRequest.url} failed after ${duration}ms:`, error.message);
    }
    
    // Check if this is a public endpoint - don't redirect on 401 for public endpoints
    const isPublicEndpoint = (originalRequest as any)?.isPublic;
    
    // Log error details for debugging public endpoints
    if (isPublicEndpoint) {
      console.debug('Public endpoint error:', {
        url: originalRequest.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
    
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      originalRequest._retry = true;
      
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token, refresh_token } = response.data;
          tokenManager.setTokens(access_token, refresh_token);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          tokenManager.clearTokens();
          tokenManager.clearTenantId();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        tokenManager.clearTokens();
        tokenManager.clearTenantId();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // Auth endpoints
  auth: {
    login: (email: string, password: string) =>
      apiClient.post('/api/auth/login', { email, password }),
    
    logout: () =>
      apiClient.post('/api/auth/logout'),
    
    refresh: (refreshToken: string) =>
      apiClient.post('/api/auth/refresh', { refresh_token: refreshToken }),
    
    register: (data: {
      email: string;
      first_name: string;
      last_name: string;
      password: string;
      company_name?: string;
      vertical_pack?: string;
      phone_number: string;
      sms_consent?: boolean;
      plan?: string;
      is_invite?: boolean;
    }) =>
      apiClient.post('/api/auth/register', data),
    
    verifyEmail: (token: string) =>
      apiClient.post('/api/auth/verify-email', { token }),
    
    resendVerification: (email: string) =>
      apiClient.post('/api/auth/resend-verification', { email }),
    
    changePassword: (currentPassword: string, newPassword: string) =>
      apiClient.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    
    requestPasswordReset: (email: string) =>
      apiClient.post('/api/auth/password-reset', { email }),
    
    confirmPasswordReset: (token: string, newPassword: string) =>
      apiClient.post('/api/auth/password-reset/confirm', {
        token,
        new_password: newPassword,
      }),
  },
  
  // User endpoints
  user: {
    getProfile: () => apiClient.get('/api/auth/profile'),
    updateProfile: (data: any) => apiClient.put('/api/auth/profile', data),
  },
  
  // Tenant endpoints
  tenant: {
    getAll: () => apiClient.get('/api/tenants/'),
    getCurrent: () => apiClient.get('/api/tenants/current'),
    update: (data: any) => apiClient.put('/api/tenants/current', data),
    getMembers: (tenantId: string) => apiClient.get(`/api/tenants/${tenantId}/members`),
    updateMemberRole: (tenantId: string, userId: string, role: string) => 
      apiClient.put(`/api/tenants/${tenantId}/members/${userId}/role`, { role }),
    removeMember: (tenantId: string, userId: string) => 
      apiClient.delete(`/api/tenants/${tenantId}/members/${userId}`),
  },
  
  // Contacts endpoints
  contacts: {
    list: (params?: any) => apiClient.get('/api/contacts/', { params }),
    get: (id: string) => apiClient.get(`/api/contacts/${id}`),
    create: (data: any) => apiClient.post('/api/contacts/', data),
    update: (id: string, data: any) => apiClient.put(`/api/contacts/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/contacts/${id}`),
    importContacts: (data: any) => apiClient.post('/api/contacts/import', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
    exportContacts: (params?: any) => apiClient.get('/api/contacts/export', { 
      params,
      responseType: 'blob'
    }),
    validateImport: (file: FormData) => apiClient.post('/api/contacts/validate-import', file, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    bulkDelete: (contactIds: string[]) => apiClient.post('/api/contacts/bulk-delete', {
      contact_ids: contactIds
    }),
    bulkUpdate: (contactIds: string[], updateData: any) => apiClient.post('/api/contacts/bulk-update', {
      contact_ids: contactIds,
      update_data: updateData
    }),
    bulkTag: (contactIds: string[], addTags?: string[], removeTags?: string[]) => apiClient.post('/api/contacts/bulk-tag', {
      contact_ids: contactIds,
      add_tags: addTags || [],
      remove_tags: removeTags || []
    }),
  },

  // Imports endpoints (for CSV with column mappings)
  imports: {
    previewCsv: (file: FormData, presetMappings?: any[]) => {
      if (presetMappings) {
        file.append('preset_mappings', JSON.stringify(presetMappings));
      }
      return apiClient.post('/api/imports/csv/preview', file, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    processCsv: (file: FormData, columnMappings: any[], options?: { dry_run?: boolean; skip_duplicates?: boolean; update_existing?: boolean }) => {
      const formData = new FormData();
      formData.append('file', file.get('file') as File);
      formData.append('column_mappings', JSON.stringify(columnMappings));
      if (options?.dry_run !== undefined) formData.append('dry_run', String(options.dry_run));
      if (options?.skip_duplicates !== undefined) formData.append('skip_duplicates', String(options.skip_duplicates));
      if (options?.update_existing !== undefined) formData.append('update_existing', String(options.update_existing));
      return apiClient.post('/api/imports/csv/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
  },

  // Consent endpoints
  consent: {
    getEvents: (params?: { status?: string; channel?: string; limit?: number; offset?: number; sort_by?: string; sort_order?: string }) => 
      apiClient.get('/api/consent/events', { params }),
    getContactEvents: (contactId: string, params?: { limit?: number; channel?: string }) => 
      apiClient.get(`/api/consent/contact/${contactId}`, { params }),
    getLatestConsent: (contactId: string, channel?: string) => 
      apiClient.get(`/api/consent/contact/${contactId}/latest`, { params: channel ? { channel } : {} }),
    getConsentStatus: (contactId: string, channel?: string) => 
      apiClient.get(`/api/consent/contact/${contactId}/status`, { params: channel ? { channel } : {} }),
    withdrawConsent: (contactId: string, channel: string, source?: string) => 
      apiClient.post(`/api/consent/contact/${contactId}/withdraw`, {}, { params: { channel, ...(source ? { source } : {}) } }),
    grantConsent: (contactId: string, channel: string, source?: string, ipAddress?: string, userAgent?: string) =>
      apiClient.post(`/api/consent/contact/${contactId}/grant`, {}, { 
        params: { 
          channel, 
          ...(source ? { source } : {}),
          ...(ipAddress ? { ip_address: ipAddress } : {}),
          ...(userAgent ? { user_agent: userAgent } : {})
        } 
      }),
    createEvent: (data: any) => apiClient.post('/api/consent/', data),
  },
  
  // Compliance endpoints
  compliance: {
    getAttestationStatus: () => apiClient.get('/compliance/attestation/status'),
    createAttestation: (data: { 
      attestation_type: string; 
      attestation_text: string; 
      accepted: boolean 
    }) => apiClient.post('/compliance/attestation', data),
    getDashboard: () => apiClient.get('/compliance/dashboard'),
    getMetrics: (days?: number) => apiClient.get('/compliance/metrics', { params: { days } }),
    validateContacts: (data: any) => apiClient.post('/compliance/validate-contacts', data),
    checkStatus: () => apiClient.get('/compliance/check-status'),
    getReport: (startDate?: string, endDate?: string) => 
      apiClient.get('/compliance/report', { params: { start_date: startDate, end_date: endDate } }),
  },
  
  // Campaigns endpoints
  campaigns: {
    list: (params?: any) => apiClient.get('/campaigns/', { params }),
    get: (id: string) => apiClient.get(`/campaigns/${id}`),
    create: (data: any) => apiClient.post('/campaigns/', data),
    update: (id: string, data: any) => apiClient.put(`/campaigns/${id}`, data),
    delete: (id: string) => apiClient.delete(`/campaigns/${id}`),
    validate: (id: string) => apiClient.get(`/campaigns/${id}/validate`),
    getContacts: (id: string, status?: string, limit?: number, offset?: number) => 
      apiClient.get(`/campaigns/${id}/contacts`, { 
        params: { status, limit, offset } 
      }),
    getMetrics: (id: string) => apiClient.get(`/campaigns/${id}/metrics`),
    getWebhooks: (id: string) => apiClient.get(`/campaigns/${id}/webhooks`),
    getWebhookEvents: (id: string, limit?: number) => 
      apiClient.get(`/campaigns/${id}/webhook-events`, { params: { limit } }),
    enqueue: (id: string, data?: any) => apiClient.post(`/campaigns/${id}/enqueue`, data || {}),
    pause: (id: string) => apiClient.post(`/campaigns/${id}/pause`),
    resume: (id: string) => apiClient.post(`/campaigns/${id}/resume`),
    cancel: (id: string) => apiClient.post(`/campaigns/${id}/cancel`),
    getStatus: (id: string) => apiClient.get(`/campaigns/${id}/status`),
  },
  
  // Sequences endpoints
  sequences: {
    list: (params?: any) => apiClient.get('/sequences/', { params }),
    get: (id: string) => apiClient.get(`/sequences/${id}`),
    create: (data: any) => apiClient.post('/sequences/', data),
    update: (id: string, data: any) => apiClient.put(`/sequences/${id}`, data),
    delete: (id: string) => apiClient.delete(`/sequences/${id}`),
    // Step management
    getSteps: (id: string) => apiClient.get(`/sequences/${id}/steps`),
    addStep: (id: string, stepData: any) => apiClient.post(`/sequences/${id}/steps`, stepData),
    updateStep: (stepId: string, stepData: any) => apiClient.put(`/sequences/steps/${stepId}`, stepData),
    deleteStep: (stepId: string) => apiClient.delete(`/sequences/steps/${stepId}`),
    reorderSteps: (id: string, stepOrders: Record<string, number>) =>
      apiClient.post(`/sequences/${id}/steps/reorder`, stepOrders),
    // Validation and utilities
    validate: (id: string) => apiClient.get(`/sequences/${id}/validate`),
    duplicate: (id: string, newName: string) =>
      apiClient.post(`/sequences/${id}/duplicate`, null, { params: { new_name: newName } }),
  },
  
  // Templates endpoints
  templates: {
    list: (params?: any) => apiClient.get('/templates/', { params }),
    get: (id: string) => apiClient.get(`/templates/${id}`),
    create: (data: any) => apiClient.post('/templates/', data),
    update: (id: string, data: any) => apiClient.put(`/templates/${id}`, data),
    delete: (id: string) => apiClient.delete(`/templates/${id}`),
    render: (id: string, data: any) => apiClient.post(`/templates/${id}/render`, data),
    preview: (id: string, data: any) => apiClient.post(`/templates/${id}/preview`, data),
    validate: (id: string) => apiClient.get(`/templates/${id}/validate`),
    duplicate: async (id: string) => {
      // Client-side duplication since backend doesn't have duplicate endpoint
      const response = await apiClient.get(`/templates/${id}`);
      const original = response.data;
      const duplicated = {
        name: `${original.name} (Copy)`,
        description: original.description,
        template_type: original.template_type,
        status: 'draft',
        subject: original.subject,
        content: original.content,
        variables: original.variables,
        preview_data: original.preview_data,
      };
      return apiClient.post('/templates/', duplicated);
    },
  },
  
  // Feedback endpoints
  feedback: {
    list: (params?: any) => {
      // Filter out empty string values from params
      const filteredParams = params ? Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      ) : {};
      return apiClient.get('/api/feedback', { params: filteredParams });
    },
    get: (id: string) => apiClient.get(`/api/feedback/${id}`),
    create: (data: any) => apiClient.post('/api/feedback', data),
    update: (id: string, data: any) => apiClient.put(`/api/feedback/${id}`, data),
    resolve: (id: string, data: any) => apiClient.post(`/api/feedback/${id}/resolve`, data),
    assign: (id: string, data: any) => apiClient.post(`/api/feedback/${id}/assign`, data),
    bulkUpdate: (data: any) => apiClient.post('/api/feedback/bulk-update', data),
    getOverdue: () => apiClient.get('/api/feedback/overdue'),
    getComments: (feedbackId: string) => apiClient.get(`/api/feedback/${feedbackId}/comments`),
    addComment: (feedbackId: string, data: any) => apiClient.post(`/api/feedback/${feedbackId}/comments`, data),
    updateSLA: (feedbackId: string, data: any) => apiClient.put(`/api/feedback/${feedbackId}/sla`, data),
    getStatsSummary: () => apiClient.get('/api/feedback/stats/summary'),
    getAnalytics: (data: any) => apiClient.post('/api/feedback/analytics', data),
  },
  
  // Analytics endpoints
  analytics: {
    getDashboardKPIs: () => apiClient.get('/analytics/dashboard-kpis'),
    getOverview: (params?: any) => apiClient.get('/analytics/overview', { params }),
    getRecentActivities: (limit?: number) => apiClient.get('/analytics/recent-activities', { params: { limit } }),
    getCampaignAnalytics: (params?: any) => apiClient.get('/analytics/campaigns', { params }),
    getFeedbackAnalytics: (params?: any) => apiClient.get('/analytics/feedback-breakdown', { params }),
    getClickAnalytics: (params?: any) => apiClient.get('/analytics/clicks', { params }),
    exportData: (exportRequest: any) => apiClient.post('/analytics/export', exportRequest),
    listExports: (params?: any) => apiClient.get('/analytics/exports', { params }),
    getExportStatus: (jobId: string) => apiClient.get(`/analytics/exports/${jobId}`),
    downloadExport: (jobId: string) => apiClient.get(`/analytics/exports/${jobId}/download`, { responseType: 'blob' }),
    createSnapshot: () => apiClient.post('/analytics/snapshots/create'),
    listSnapshots: (params?: any) => apiClient.get('/analytics/snapshots', { params }),
    cleanupSnapshots: (days?: number) => apiClient.delete('/analytics/snapshots/cleanup', { params: { days } }),
    getGooglePlace: () => apiClient.get('/analytics/google-places/current'),
    getGoogleReviews: () => apiClient.get('/analytics/google-places/reviews'),
    searchGooglePlace: (data: any) => apiClient.post('/analytics/google-places/search', data),
    linkGooglePlace: (placeId: string) => apiClient.post(`/analytics/google-places/link/${placeId}`),
    getVerificationStatus: () => apiClient.get('/analytics/google-places/verification'),
    retryVerification: () => apiClient.post('/analytics/google-places/verify'),
  },
  
  // Billing endpoints
  billing: {
    getInfo: () => apiClient.get('/billing/info'),
    getUsage: () => apiClient.get('/billing/usage'),
    getPlans: () => apiClient.get('/billing/plans'),
    getInvoices: (limit?: number) => apiClient.get('/billing/invoices', { params: { limit } }),
    updatePlan: (plan: string) => apiClient.post('/billing/plan', { plan }),
    createPortalSession: (returnUrl: string) => apiClient.post('/billing/portal', { return_url: returnUrl }),
    createCheckoutSession: (plan: string, successUrl: string, cancelUrl: string) => 
      apiClient.post('/billing/checkout', { plan, success_url: successUrl, cancel_url: cancelUrl }),
    cancelSubscription: () => apiClient.post('/billing/cancel'),
    setupBilling: (data: any) => apiClient.post('/billing/setup', data),
    checkUsageLimits: (metricType: string, additionalQuantity?: number) => 
      apiClient.get(`/billing/usage/check/${metricType}`, { params: { additional_quantity: additionalQuantity } }),
    getFeatures: () => apiClient.get('/billing/features'),
  },
  
  // Branding endpoints
  branding: {
    get: () => apiClient.get('/branding/'),
    update: (data: any) => apiClient.put('/branding/', data),
    create: (data: any) => apiClient.post('/branding/', data),
    delete: () => apiClient.delete('/branding/'),
    verifyDomainStart: (domain: string, verificationType: string) =>
      apiClient.post('/branding/domain/verify/start', { domain, verification_type: verificationType }),
    verifyDomainCheck: (domain: string, verificationType: string) =>
      apiClient.post('/branding/domain/verify/check', { domain, verification_type: verificationType }),
    checkDNS: (domain: string, recordType: string) =>
      apiClient.post('/branding/dns/check', { domain, record_type: recordType }),
    getDNSHealth: () => apiClient.get('/branding/dns/health'),
    preview: (data: any) => apiClient.post('/branding/preview', data),
    getDomainSuggestions: () => apiClient.get('/branding/domains/suggestions'),
    // Setup wizard endpoints
    getWizardStatus: () => apiClient.get('/branding/setup-wizard/status'),
    getWizardStep: (step: number) => apiClient.get(`/branding/setup-wizard/step/${step}`),
    saveWizardStep: (step: number, data: any) => apiClient.post(`/branding/setup-wizard/step/${step}`, data),
  },
  
  // Rating Gate endpoints
  ratingGate: {
    getSettings: () => apiClient.get('/rating-gate/settings'),
    getSettingsPublic: (campaignId?: string, contactId?: string) => {
      const params: any = {};
      if (campaignId) params.campaign_id = campaignId;
      if (contactId) params.contact_id = contactId;
      // Mark as public endpoint to skip auth headers and redirects
      return apiClient.get('/rating-gate/settings/public', { 
        params,
        isPublic: true 
      } as any);
    },
    createSettings: (data: any) => apiClient.post('/rating-gate/settings', data),
    updateSettings: (data: any) => apiClient.put('/rating-gate/settings', data),
    submitRating: (data: any) => {
      // Add campaign_id and contact_id as query params to help with tenant resolution
      const params: any = {};
      if (data.campaign_id) params.campaign_id = data.campaign_id;
      if (data.contact_id) params.contact_id = data.contact_id;
      // Mark as public endpoint to skip auth headers and redirects
      return apiClient.post('/rating-gate/submit', data, { 
        params,
        isPublic: true 
      } as any);
    },
    searchPlaces: (query: string, location?: string) =>
      apiClient.post('/rating-gate/search-places', { query, location }),
    generateDeepLink: (platform: string, placeId?: string) =>
      apiClient.post('/rating-gate/generate-link', { platform, place_id: placeId }),
    getPolicyCopy: () => apiClient.get('/rating-gate/policy-copy'),
  },
  
  // Vertical Packs endpoints
  verticalPacks: {
    list: () => apiClient.get('/api/vertical-packs/'),
    get: (industry: string) => apiClient.get(`/api/vertical-packs/${industry}`),
    getImportMappings: (industry: string) =>
      apiClient.get(`/api/vertical-packs/${industry}/import-mappings`),
    apply: (industry: string, updateExisting: boolean = false) =>
      apiClient.post('/api/vertical-packs/apply', { industry, update_existing: updateExisting }),
    getTenantInfo: () => apiClient.get('/api/vertical-packs/tenant/current'),
  },

  // Automation endpoints
  automation: {
    // Webhooks
    createWebhook: (data: any) => apiClient.post('/api/automation/webhooks', data),
    getWebhooks: (tenantId: string) => apiClient.get('/api/automation/webhooks', { params: { tenant_id: tenantId } }),
    getWebhook: (webhookId: string) => apiClient.get(`/api/automation/webhooks/${webhookId}`),
    updateWebhook: (webhookId: string, data: any) => apiClient.put(`/api/automation/webhooks/${webhookId}`, data),
    deleteWebhook: (webhookId: string) => apiClient.delete(`/api/automation/webhooks/${webhookId}`),
    getWebhookEvents: (webhookId: string, limit?: number) => 
      apiClient.get(`/api/automation/webhooks/${webhookId}/events`, { params: { limit } }),
    getRecentWebhookEvents: (limit?: number) => 
      apiClient.get(`/api/automation/webhook-events/recent`, { params: { limit } }),
    retryFailedEnqueue: (eventId: string) => 
      apiClient.post(`/api/automation/webhook-events/${eventId}/retry`),
    getWebhookMetrics: (webhookId: string) => 
      apiClient.get(`/api/automation/webhooks/${webhookId}/metrics`),
    testWebhook: (webhookId: string, testData: any) => 
      apiClient.post(`/api/automation/webhooks/${webhookId}/test`, { webhook_id: webhookId, test_data: testData }),

    getWebhookTemplates: (vertical?: string) => 
      apiClient.get(`/api/automation/templates${vertical ? `?vertical=${vertical}` : ''}`),
    
    cloneWebhook: (webhookId: string, newName: string) => 
      apiClient.post(`/api/automation/webhooks/${webhookId}/clone?new_name=${encodeURIComponent(newName)}`),
    
    createWebhookFromTemplate: (templateId: string, webhookData: any) => 
      apiClient.post(`/api/automation/templates/${templateId}/create`, webhookData),
    
    // Mapping presets
    createPreset: (data: any) => apiClient.post('/api/automation/presets', data),
    getTenantPresets: (tenantId: string, niche?: string) => 
      apiClient.get(`/api/automation/presets/tenant/${tenantId}`, { params: niche ? { niche } : {} }),
    getPreset: (presetId: string) => apiClient.get(`/api/automation/presets/${presetId}`),
    updatePreset: (presetId: string, data: any) => apiClient.put(`/api/automation/presets/${presetId}`, data),
    deletePreset: (presetId: string) => apiClient.delete(`/api/automation/presets/${presetId}`),
    
    // System preset templates
    getSystemPresetTemplates: (niche?: string) => 
      apiClient.get('/api/automation/presets/system', { params: niche ? { niche } : {} }),
  },

  // Test Data endpoints
  testData: {
    generate: () => apiClient.post('/test-data/generate'),
    clear: () => apiClient.delete('/test-data/clear'),
  },

  // Invitations endpoints
  invitations: {
    list: () => apiClient.get('/api/invites/'),
    get: (inviteId: string) => apiClient.get(`/api/invites/${inviteId}`),
    getByToken: (token: string) => apiClient.get(`/api/invites/public/${token}`),
    create: (data: any) => apiClient.post('/api/invites/', data),
    update: (inviteId: string, data: any) => apiClient.put(`/api/invites/${inviteId}`, data),
    revoke: (inviteId: string) => apiClient.post(`/api/invites/${inviteId}/revoke`),
    resend: (inviteId: string) => apiClient.post(`/api/invites/${inviteId}/resend`),
    accept: (inviteId: string) => apiClient.post(`/api/invites/${inviteId}/accept`),
  },

  // API Keys endpoints
  apiKeys: {
    list: (tenantId: string, includeRevoked?: boolean) => 
      apiClient.get('/api/api-keys', { params: { tenant_id: tenantId, include_revoked: includeRevoked } }),
    get: (keyId: string) => apiClient.get(`/api/api-keys/${keyId}`),
    create: (data: { name: string; tenant_id: string; scopes?: string[] }) => 
      apiClient.post('/api/api-keys', data),
    update: (keyId: string, data: any) => apiClient.patch(`/api/api-keys/${keyId}`, data),
    revoke: (keyId: string) => apiClient.delete(`/api/api-keys/${keyId}`),
  },

  // Integration Settings endpoints
  integrations: {
    get: () => apiClient.get('/integration-settings'),
    update: (data: any) => apiClient.put('/integration-settings', data),
    testEmail: (data: { to_email: string; subject?: string; message?: string }) => 
      apiClient.post('/integration-settings/test-email', data),
    testSms: (data: { to_phone: string; message?: string }) => 
      apiClient.post('/integration-settings/test-sms', data),
  },

  // Server Admin endpoints (superuser only)
  serverAdmin: {
    // Server settings
    getServerSettings: () => apiClient.get('/api/server/settings'),
    getStripeSettings: () => apiClient.get('/api/server/settings/stripe'),
    updateStripeSettings: (data: any) => apiClient.put('/api/server/settings/stripe', data),
    getEmailSettings: () => apiClient.get('/api/server/settings/registration-email'),
    updateEmailSettings: (data: any) => apiClient.put('/api/server/settings/registration-email', data),
    getGooglePlacesSettings: () => apiClient.get('/api/server/settings/google-places'),
    updateGooglePlacesSettings: (data: any) => apiClient.put('/api/server/settings/google-places', data),
    getTwilioSettings: () => apiClient.get('/api/server/settings/twilio'),
    updateTwilioSettings: (data: any) => apiClient.put('/api/server/settings/twilio', data),
    getPricingSettings: () => apiClient.get('/api/server/settings/pricing'),
    updatePricingSettings: (data: any) => apiClient.put('/api/server/settings/pricing', data),
    getDeepLinkVerificationBypass: () => apiClient.get('/api/server/settings/deep-link-verification-bypass'),
    updateDeepLinkVerificationBypass: (data: { enabled: boolean }) => apiClient.put('/api/server/settings/deep-link-verification-bypass', data),
    getCampaignSettingsLock: () => apiClient.get('/api/server/settings/campaign-settings-lock'),
    updateCampaignSettingsLock: (data: { enabled: boolean }) => apiClient.put('/api/server/settings/campaign-settings-lock', data),
    getRatingGateModeLock: () => apiClient.get('/api/server/settings/rating-gate-mode-lock'),
    updateRatingGateModeLock: (data: { enabled: boolean }) => apiClient.put('/api/server/settings/rating-gate-mode-lock', data),
    getCardRequiredForTrial: () => apiClient.get('/api/server/settings/card-required-for-trial'),
    updateCardRequiredForTrial: (data: { enabled: boolean }) => apiClient.put('/api/server/settings/card-required-for-trial', data),
    getPublicSettings: () => apiClient.get('/api/server/settings/public'),
    setPublicSetting: (key: string, data: any) => apiClient.post(`/api/server/settings/public/${key}`, data),
    
    // Server statistics
    getStats: () => apiClient.get('/api/server/stats'),
    
    // User management
    getUsers: () => apiClient.get('/api/server/users'),
    updateUserStatus: (userId: string, data: any) => apiClient.put(`/api/server/users/${userId}/status`, data),
    updateUserRole: (userId: string, data: any) => apiClient.put(`/api/server/users/${userId}/role`, data),
    updateUserTenantRole: (userId: string, tenantId: string, data: any) => apiClient.put(`/api/server/users/${userId}/tenant-role/${tenantId}`, data),
    assignUserToTenant: (userId: string, data: { tenant_id: string; role: string }) => apiClient.post(`/api/server/users/${userId}/assign-tenant`, data),
    deleteUser: (userId: string) => apiClient.delete(`/api/server/users/${userId}`),
    
    // Tenant management
    getTenants: () => apiClient.get('/api/server/tenants'),
    updateTenantStatus: (tenantId: string, data: any) => apiClient.put(`/api/server/tenants/${tenantId}/status`, data),
    deleteTenant: (tenantId: string) => apiClient.delete(`/api/server/tenants/${tenantId}`),
    
    // Compliance management
    getComplianceOverview: () => apiClient.get('/api/server/compliance/overview'),
    getComplianceTenants: (params?: any) => apiClient.get('/api/server/compliance/tenants', { params }),
    getTenantComplianceDetails: (tenantId: string) => apiClient.get(`/api/server/compliance/tenant/${tenantId}`),
    getComplianceAlerts: () => apiClient.get('/api/server/compliance/alerts'),
    suspendTenantForCompliance: (tenantId: string, reason: string) => apiClient.post(`/api/server/compliance/suspend/${tenantId}`, { reason }),
    getPlatformComplianceReport: (params?: any) => apiClient.get('/api/server/compliance/report', { params }),
    seedComplianceTestData: (payload: { tenant_id: string; messages?: number; opt_out_rate?: number; spam_rate?: number; delivery_failure_rate?: number; days?: number; }) =>
      apiClient.post('/api/server/compliance/test-data/seed', payload),
    clearComplianceTestData: (tenantId: string, days: number = 35) =>
      apiClient.post('/api/server/compliance/test-data/clear', { tenant_id: tenantId, days }),
    
    // Diagnostics
    getEmailDiagnostics: (tenantId?: string) => 
      apiClient.get('/api/server/diagnostics/email', { params: tenantId ? { tenant_id: tenantId } : {} }),
    
    // Support Emails
    getSupportEmails: (params?: { to_email?: string; status?: string; limit?: number; offset?: number }) =>
      apiClient.get('/api/server/support-emails', { params }),
    getSupportEmail: (emailId: string) =>
      apiClient.get(`/api/server/support-emails/${emailId}`),
    replyToSupportEmail: (emailId: string, data: { subject: string; body: string; html_body?: string }) =>
      apiClient.post(`/api/server/support-emails/${emailId}/reply`, data),
    markSupportEmailRead: (emailId: string) =>
      apiClient.post(`/api/server/support-emails/${emailId}/read`),
    archiveSupportEmail: (emailId: string) =>
      apiClient.post(`/api/server/support-emails/${emailId}/archive`),
    sendTestEmail: (data: { to_email: string; subject?: string; html_body?: string; text_body?: string }) =>
      apiClient.post('/api/server/support-emails/send-test', data),
    getSupportEmailsDiagnostics: () =>
      apiClient.get('/api/server/support-emails/diagnostics'),
    getWebhookInfo: () =>
      apiClient.get('/api/server/support-emails/webhook-info'),
    fixExistingSupportEmails: () =>
      apiClient.post('/api/server/support-emails/fix-existing'),
  },
};

export default apiClient;
